import { hasPhantomEthereum } from "@/lib/wallet/phantom-evm";
import { isGenuinePhantom } from "@/lib/wallet/phantom-genuine";

/**
 * Whether RainbowKit's "Browser Wallet" would connect Phantom's Ethereum side,
 * which `phantomEntry` exists to keep gated.
 *
 * RainbowKit builds a wagmi connector for every listed entry, installed or
 * not (`installed: false` only hides the row), and Browser Wallet's is
 * *targeted*: `getInjectedConnector({})` hands wagmi the provider in front —
 * `window.ethereum.providers[0]`, else `window.ethereum` — as its target. So
 * wagmi attaches its `connect` and `accountsChanged` listeners to that
 * provider in `setup()`, and `reconnect` adopts it on `eth_accounts` alone.
 * Once a holder has connected Phantom for Solana, Phantom answers that with
 * an account, and both routes took its EVM side around `phantomEntry`'s gate:
 * the claim page checked an address the holder never picked, and an account
 * imported from a Solana key met Phantom's blocking "Unsupported account"
 * window on every page load.
 *
 * Only where Phantom's own entry can reach it (`window.phantom.ethereum`), so
 * nothing becomes unreachable: a Phantom without that namespace keeps Browser
 * Wallet as its route. A property read, never a request.
 */
export function browserWalletReachesPhantom(): boolean {
  if (typeof window === "undefined" || !hasPhantomEthereum()) return false;
  const injected: unknown = Reflect.get(window, "ethereum");
  if (typeof injected !== "object" || injected === null) return false;
  // As RainbowKit picks it: the first of a non-empty `providers`, else the
  // object itself.
  const providers: unknown = Reflect.get(injected, "providers");
  const front: unknown =
    Array.isArray(providers) && providers.length > 0 ? providers[0] : injected;
  // Not the bare flag: OKX and Bitget set `isPhantom` too, and withdrawing
  // Browser Wallet in front of them would take their only EVM route away.
  return isGenuinePhantom(front);
}
